(() => {
  if (window.boosthausProductPageRuntime) {
    window.boosthausProductPageRuntime.initialize();
    return;
  }

  const boundRoots = new WeakSet();
  const instances = new WeakMap();

  class BoosthausProductPage {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.mainButton = root.querySelector(`#ProductSubmitButton-${this.sectionId}`);
      this.nativeForm = this.mainButton?.closest('product-form')?.querySelector('form');
      this.stickyBar = root.querySelector('[data-boosthaus-sticky-cart]');
      this.stickyButton = root.querySelector('[data-boosthaus-sticky-submit]');
      this.feedback = root.querySelector('[data-boosthaus-feedback]');
      this.lastAddSource = 'main';
      this.pendingAddVariantId = null;
      this.pendingAddPrice = null;
      this.proxyingStickyClick = false;
      this.mainButtonIsVisible = false;
      this.mainButtonIsAbove = false;
      this.overlayIsOpen = false;
      this.feedbackTimer = null;

      this.bindActions();
      this.bindAccordions();
      this.bindVariantEvents();
      this.bindCartEvents();
      this.observeMainButton();
      this.observePrice();
      this.observeOverlays();
      this.syncPurchaseState();

      if (this.stickyBar) this.stickyBar.hidden = false;
    }

    bindActions() {
      this.root.addEventListener('click', (event) => {
        const link = event.target.closest('[data-boosthaus-description-link]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const details = document.getElementById(link.hash.slice(1));
        if (!details || !this.root.contains(details)) return;
        event.preventDefault();
        details.open = true;
        details.querySelector('summary')?.focus({ preventScroll: true });
        details.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      });

      this.mainButton?.addEventListener('click', () => {
        if (this.proxyingStickyClick) return;
        this.lastAddSource = 'main';
        this.trackPendingAdd();
        this.publish('boosthaus_main_add_to_cart_clicked', { event_source: 'main_product_form' });
      });

      this.nativeForm?.addEventListener('submit', () => {
        if (!this.pendingAddVariantId) {
          this.lastAddSource = 'main';
          this.trackPendingAdd();
        }
      });

      this.stickyButton?.addEventListener('click', () => {
        if (!this.mainButton || this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') return;
        this.lastAddSource = 'sticky';
        this.trackPendingAdd();
        this.publish('boosthaus_sticky_add_to_cart_clicked', { event_source: 'sticky_mobile_cart' });
        this.proxyingStickyClick = true;
        this.mainButton.click();
        this.proxyingStickyClick = false;
      });

      this.root.querySelectorAll('[data-boosthaus-viber]').forEach((link) => {
        link.addEventListener('click', () => {
          const message = link.dataset.viberMessage;
          if (message && navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(message).then(
              () => this.showFeedback('Данните за продукта са копирани. Постави ги във Viber.'),
              () => undefined
            );
          }
          this.publish('boosthaus_fitment_viber_click', { event_source: 'product_fitment_action' });
        });
      });

      this.root.querySelectorAll('[data-boosthaus-reviews-link]').forEach((link) => {
        link.addEventListener('click', () => {
          this.publish('boosthaus_review_summary_clicked', { event_source: 'product_identity' });
        });
      });
    }

    bindAccordions() {
      this.root.querySelectorAll('details[data-boosthaus-accordion]').forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          const accordion = details.dataset.boosthausAccordion;
          const eventName = accordion === 'fitment' ? 'boosthaus_fitment_module_opened' : 'boosthaus_product_accordion_opened';
          this.publish(eventName, { accordion, event_source: 'product_page' });
        });
      });
    }

    bindVariantEvents() {
      this.root.addEventListener('boosthaus:variant-unavailable', (event) => {
        if (event.detail?.sectionId !== this.sectionId) return;
        this.root.dataset.selectedVariantId = '';
        this.root.dataset.selectedVariantPrice = '0';
        this.syncPurchaseState(null, event.detail?.label);
      });

      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.sectionId !== this.sectionId) return;
        const variant = event.data.variant;
        if (!variant) {
          this.root.dataset.selectedVariantId = '';
          this.root.dataset.selectedVariantPrice = '0';
          this.syncPurchaseState(null);
          return;
        }

        this.root.dataset.selectedVariantId = variant.id;
        this.root.dataset.selectedVariantPrice = variant.price;
        this.syncPurchaseState(variant);
        this.publish('boosthaus_variant_selected', {
          event_source: 'variant_selector',
          variant_title: variant.title,
        });
      });
    }

    bindCartEvents() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'product-form' || !this.isPendingAddEvent(event)) return;
        this.publish('boosthaus_add_to_cart_succeeded', {
          event_source: this.lastAddSource,
          variant_id: this.pendingAddVariantId,
          price: Number(this.pendingAddPrice || 0) / 100,
        });
        this.showFeedback('Продуктът е добавен в количката.');
        this.pendingAddVariantId = null;
        this.pendingAddPrice = null;
      });
      this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        if (event?.source !== 'product-form' || !this.isPendingAddEvent(event)) return;
        this.publish('boosthaus_add_to_cart_failed', {
          event_source: this.lastAddSource,
          error_type: event.source,
          variant_id: this.pendingAddVariantId,
          price: Number(this.pendingAddPrice || 0) / 100,
        });
        this.showFeedback('Не успяхме да добавим продукта. Опитай отново.', true);
        this.pendingAddVariantId = null;
        this.pendingAddPrice = null;
      });
    }

    observeMainButton() {
      if (!this.mainButton || !this.stickyBar) return;

      this.onViewportChange = () => {
        if (this.viewportFrame) return;
        this.viewportFrame = window.requestAnimationFrame(() => {
          this.viewportFrame = null;
          this.updateMainButtonPosition();
        });
      };

      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange);

      if ('IntersectionObserver' in window) {
        this.mainButtonObserver = new IntersectionObserver(() => this.updateMainButtonPosition(), { threshold: 0.15 });
        this.mainButtonObserver.observe(this.mainButton);
      }

      this.updateMainButtonPosition();
    }

    updateMainButtonPosition() {
      const rect = this.mainButton?.getBoundingClientRect();
      if (!rect) return;
      this.mainButtonIsVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      this.mainButtonIsAbove = rect.bottom <= 0;
      this.updateStickyVisibility();
    }

    observePrice() {
      const priceRoot = this.root.querySelector(`#price-${this.sectionId}`);
      if (!priceRoot || !('MutationObserver' in window)) return;
      this.priceObserver = new MutationObserver(() => this.syncStickyPrice());
      this.priceObserver.observe(priceRoot, { childList: true, characterData: true, subtree: true });
    }

    observeOverlays() {
      if (!this.stickyBar || !('MutationObserver' in window)) return;
      const update = () => {
        this.overlayIsOpen = Boolean(
          document.querySelector(
            'cart-notification.active, cart-drawer.active, .product-media-modal[open], .product-popup-modal[open], dialog[open]'
          )
        );
        this.updateStickyVisibility();
      };
      this.overlayObserver = new MutationObserver(update);
      this.overlayObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'open'],
        subtree: true,
      });
      update();
    }

    syncPurchaseState(variant, unavailableLabel = 'Недостъпна комбинация') {
      const isUnavailableCombination = variant === null;
      const available = isUnavailableCombination
        ? false
        : variant
          ? Boolean(variant.available)
          : !this.mainButton?.disabled;
      const addToCartLabel = this.root.dataset.addToCartLabel || 'Добави в количката';
      const mainLabel = this.mainButton?.querySelector(':scope > span');

      if (available && mainLabel) mainLabel.textContent = addToCartLabel;
      if (this.stickyButton) this.stickyButton.disabled = !available;

      const stickyVariant = this.root.querySelector('[data-boosthaus-sticky-variant]');
      const variantTitle = variant?.title || this.currentVariantLabel();
      if (stickyVariant) {
        const shouldHide = !variantTitle || variantTitle === 'Default Title';
        stickyVariant.hidden = shouldHide;
        stickyVariant.textContent = shouldHide ? '' : variantTitle;
      }

      this.updateViberMessage(variantTitle, variant?.id || this.root.dataset.selectedVariantId);
      if (isUnavailableCombination) {
        this.syncUnavailableCombination(unavailableLabel);
      } else {
        this.syncStickyPrice();
      }
      this.syncStickyAvailability();
    }

    syncUnavailableCombination(labelText) {
      const availability = this.root.querySelector('[data-boosthaus-availability]');
      if (availability) {
        availability.classList.remove(
          'boosthaus-product-availability--available',
          'boosthaus-product-availability--order'
        );
        availability.classList.add('boosthaus-product-availability--unavailable');
        const label = availability.querySelector('.boosthaus-product-availability__label');
        if (label) label.textContent = labelText;
        availability.querySelectorAll('.boosthaus-product-availability__details').forEach((details) => {
          details.hidden = true;
        });
      }

      const stickyPrice = this.root.querySelector('[data-boosthaus-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = '—';
    }

    syncStickyPrice() {
      const stickyPrice = this.root.querySelector('[data-boosthaus-sticky-price]');
      const priceRoot = this.root.querySelector(`#price-${this.sectionId}`);
      if (!stickyPrice || !priceRoot) return;

      const salePrice = priceRoot.querySelector('.price-item--sale');
      const regularPrice = priceRoot.querySelector('.price-item--regular');
      const source = salePrice && this.isRendered(salePrice) ? salePrice : regularPrice;
      const priceText = source?.textContent?.replace(/\s+/g, ' ').trim();
      if (priceText) stickyPrice.textContent = priceText;
    }

    syncStickyAvailability() {
      const stickyAvailability = this.root.querySelector('[data-boosthaus-sticky-availability]');
      const availabilityRoot = this.root.querySelector('[data-boosthaus-availability]');
      const availabilityLabel = availabilityRoot?.querySelector('.boosthaus-product-availability__label');
      if (!stickyAvailability) return;

      stickyAvailability.textContent = availabilityLabel?.textContent?.trim() || '';
      const tone = availabilityRoot?.classList.contains('boosthaus-product-availability--unavailable')
        ? 'unavailable'
        : availabilityRoot?.classList.contains('boosthaus-product-availability--order')
          ? 'order'
          : 'available';
      stickyAvailability.dataset.tone = tone;
    }

    trackPendingAdd() {
      const variantId = this.nativeForm?.querySelector('[name="id"]')?.value;
      this.pendingAddVariantId = variantId ? String(variantId) : null;
      this.pendingAddPrice = this.root.dataset.selectedVariantPrice || null;
    }

    isPendingAddEvent(event) {
      return Boolean(
        this.pendingAddVariantId &&
          event?.productVariantId &&
          String(event.productVariantId) === this.pendingAddVariantId
      );
    }

    updateViberMessage(variantTitle, variantId) {
      const title = this.root.dataset.productTitle || '';
      const baseUrl = this.root.dataset.productUrl || window.location.href.split('?')[0];
      const url = variantId ? `${baseUrl}?variant=${variantId}` : baseUrl;
      const variant = variantTitle && variantTitle !== 'Default Title' ? ` — ${variantTitle}` : '';
      const message = `Здравейте, искам проверка за съвместимост на: ${title}${variant}. ${url}`;
      this.root.querySelectorAll('[data-boosthaus-viber]').forEach((link) => {
        link.dataset.viberMessage = message;
      });
    }

    currentVariantLabel() {
      return Array.from(this.root.querySelectorAll('variant-selects input:checked, variant-selects select'))
        .map((control) => (control.tagName === 'SELECT' ? control.selectedOptions[0]?.textContent : control.value))
        .filter(Boolean)
        .join(' / ')
        .trim();
    }

    updateStickyVisibility() {
      if (!this.stickyBar) return;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const shouldShow = isMobile && this.mainButtonIsAbove && !this.mainButtonIsVisible && !this.overlayIsOpen;
      this.stickyBar.classList.toggle('is-visible', shouldShow);
      this.stickyBar.setAttribute('aria-hidden', String(!shouldShow));
    }

    showFeedback(message, isError = false) {
      this.feedback = this.root.querySelector('[data-boosthaus-feedback]');
      if (!this.feedback) return;
      window.clearTimeout(this.feedbackTimer);
      this.feedback.textContent = message;
      this.feedback.classList.toggle('is-error', isError);
      this.feedback.hidden = false;
      this.feedbackTimer = window.setTimeout(() => {
        this.feedback.hidden = true;
      }, 6000);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.variantUnsubscriber?.();
      this.cartUpdateUnsubscriber?.();
      this.cartErrorUnsubscriber?.();
      this.mainButtonObserver?.disconnect();
      this.priceObserver?.disconnect();
      this.overlayObserver?.disconnect();
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      if (this.viewportFrame) window.cancelAnimationFrame(this.viewportFrame);
      window.clearTimeout(this.feedbackTimer);
    }

    isRendered(element) {
      return element.getClientRects().length > 0;
    }

    publish(name, extra = {}) {
      const price = Number(this.root.dataset.selectedVariantPrice || 0) / 100;
      const payload = {
        product_id: this.root.dataset.productId,
        variant_id: this.root.dataset.selectedVariantId,
        vendor: this.root.dataset.productVendor,
        product_type: this.root.dataset.productType,
        price,
        currency: this.root.dataset.currency,
        ...extra,
      };

      if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(name, payload);
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...payload });
      document.dispatchEvent(new CustomEvent(name, { detail: payload }));
    }
  }

  const rootsWithin = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-boosthaus-product-page]')) roots.push(scope);
    scope.querySelectorAll?.('[data-boosthaus-product-page]').forEach((root) => roots.push(root));
    return roots;
  };

  const teardownRoot = (root) => {
    instances.get(root)?.destroy();
    instances.delete(root);
  };

  const initialize = (scope = document) => {
    rootsWithin(scope).forEach((root) => {
      if (boundRoots.has(root)) return;
      boundRoots.add(root);
      instances.set(root, new BoosthausProductPage(root));
      root.addEventListener('boosthaus:product-disconnect', () => teardownRoot(root), { once: true });
    });
  };

  window.boosthausProductPageRuntime = { initialize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach(teardownRoot);
  });
})();
